import React from "react";
import { useNavigate } from "react-router-dom";
import { Images } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";

import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import StatusAlert from "../../../../components/alerts/StatusAlert";
import Button from "../../../../components/buttons/Button";

import AlbumThumbnailCard from "../../../../components/cards/thumbnails/albums/AlbumThumbnailCard";

import { useCreatorChannelAlbums } from "../hooks/useCreatorChannelAlbums";

import "./ChannelAlbumsView.css";

const ALBUMS_LIMIT = 12;

export default function ChannelAlbumsView({ username }) {
  const navigate = useNavigate();

  const { albums, loading, loadingMore, error, hasMore, loadMore } =
    useCreatorChannelAlbums({
      username,
      albumId: null,
      limit: ALBUMS_LIMIT,
    });

  const albumList = Array.isArray(albums) ? albums : [];

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error.message || "Unable to load albums."
    : "";

  const handleAlbumClick = (album) => {
    const albumTag = String(album?.albumTag || "").trim();

    if (!albumTag) return;

    navigate(`/albums/${encodeURIComponent(albumTag)}`);
  };

  const handleLoadMore = async () => {
    if (loading || loadingMore || !hasMore) return;

    await loadMore();
  };

  const renderAlbums = () => {
    if (loading && !albumList.length) {
      return (
        <ContentGrid
          items={[]}
          loading
          columns={4}
          tabletColumns={3}
          mobileColumns={2}
          gap={16}
          skeletonCount={ALBUMS_LIMIT}
        />
      );
    }

    if (!albumList.length) {
      return (
        <EmptyState
          icon={<Images size={28} />}
          title={errorMessage ? "Unable to load albums" : "No albums yet"}
          description={
            errorMessage ||
            "No public photo albums have been shared on this channel."
          }
        />
      );
    }

    return (
      <ContentGrid
        items={albumList}
        columns={4}
        tabletColumns={3}
        mobileColumns={2}
        gap={16}
        showEmpty={false}
        renderItem={(album) => (
          <AlbumThumbnailCard
            key={album.albumId ?? album.albumTag}
            album={album}
            onClick={() => handleAlbumClick(album)}
          />
        )}
      />
    );
  };

  return (
    <ViewContainer>
      <ViewHeader
        title="Albums"
        description="Explore photo albums shared on this channel."
      />

      {errorMessage && albumList.length > 0 && (
        <div className="channel-albums__alert">
          <StatusAlert type="error" message={errorMessage} />
        </div>
      )}

      <div className="channel-albums__content">
        {renderAlbums()}

        {hasMore && albumList.length > 0 && (
          <div className="channel-albums__footer">
            <Button
              value={loadingMore ? "Loading..." : "Load More"}
              onClick={handleLoadMore}
              disabled={loading || loadingMore}
              variant="primary-outlined"
            />
          </div>
        )}
      </div>
    </ViewContainer>
  );
}
